/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery } from 'react-query'

import { queryClient } from '@modules/query'
import type { QueryHook, QueryHookOptions } from '@modules/query/types'
import { listBudgetsV1 } from '@modules/billing-api/v1/callers'
import type { operations } from '@modules/billing-api/v1/types'

import { defaultBillingOptions } from './constants'

const QUERY_KEY = 'listBudgetsV1'

type GetBudgetsVariables = {
  organizationId: operations['listBudgetsV1']['parameters']['path']['organization_id']
}

const useGetBudgets: QueryHook<typeof listBudgetsV1> = (
  organizationId: GetBudgetsVariables['organizationId'],
  options?: QueryHookOptions,
) =>
  useQuery(
    [QUERY_KEY, organizationId],
    () => listBudgetsV1({ pathParameters: { organization_id: organizationId } }),
    {
      ...defaultBillingOptions,
      ...options,
      enabled: Boolean(organizationId),
    },
  )

export const invalidateGetBudgets = (organizationId: GetBudgetsVariables['organizationId']) => {
  const keysToInvalidate = [QUERY_KEY]

  if (organizationId) {
    keysToInvalidate.push(organizationId)
  }

  return queryClient.invalidateQueries(keysToInvalidate)
}

export default useGetBudgets
